import React from "react";
import { CloseIcon, KYCVerification } from "../../../../../assets/Icons";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import Button from "../../../../../components/ui/Button";
import { ArrowRight, Sparkles } from "lucide-react";
import Modal from "../../../../../components/ui/Modal";

interface Props {
  open: boolean;
  onYes: () => void;
  onNo: () => void;
}

export const KycAggrementModal: React.FC<Props> = ({ open, onYes, onNo }) => {
  const dispatch = useAppDispatch();

  const { calculator } = useAppSelector((state) => state.remittance);

  const [isAgreed, setIsAgreed] = React.useState(false);

  return (
    <Modal
      modalClass="bg-card border-none !p-0 rounded-xl"
      headerClass="!my-0"
      closeHandler={() => {}}
      closable={false}
      visible={open}
      maskClosable={false}
    >
      <div className="max-w-[800px] overflow-hidden border-0 bg-card p-0 shadow-[0_8px_40px_-8px_hsl(220_90%_56%/0.35)] rounded-xl">
        <div className="grid md:grid-cols-2">
          {/* Left Content Section */}
          <div className="flex flex-col justify-between p-8">
            <div>
              <div className="mb-6 text-left">
                <>
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium text-primary">Welcome</span>
                  </div>

                  <p className="text-3xl font-bold tracking-tight text-foreground">
                    Welcome to
                    <span className="mt-1 block bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
                      moneyHOP
                    </span>
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    We will be verifying your documents for KYC to provide you the seamless
                    experience.
                  </p>
                </>
              </div>

              <div className="mb-8 flex items-start gap-3 rounded-xl border border-border/50 bg-gray-100 p-4">
                <input
                  checked={isAgreed}
                  type="checkbox"
                  onChange={() => {
                    setIsAgreed(!isAgreed);
                  }}
                  className="-mt-4 h-14 w-14 rounded border-2 data-[state=checked]:border-primary data-[state=checked]:bg-primary cursor-pointer"
                />
                <label htmlFor="agreement" className="text-sm leading-relaxed text-gray-500">
                  These documents play an important role in International money transfer so uploaded
                  images should be clear and crisp or the chances of cancellation increase.
                </label>
              </div>
            </div>

            <Button
              onClickHandler={() => {
                setIsAgreed(false);
                onYes();
              }}
              disabled={!isAgreed}
              className={`w-full gap-2 rounded-xl bg-primary/90 py-3 text-lg font-semibold shadow-lg transition-all hover:bg-primary/90 hover:shadow-xl disabled:bg-gray-100 disabled:opacity-40 ${
                isAgreed ? "text-white" : "text-black"
              }`}
            >
              {"Confirm"}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {/* Right Illustration Section */}
          <div className="relative w-full max-w-[560px] rounded-l-[28px] overflow-hidden">
            {/* Background gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-sky-400/10" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,hsl(var(--primary)/0.18),transparent_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,hsl(200_85%_55%/0.12),transparent_55%)]" />

            {/* Close button */}
            <button
              onClick={() => {
                setIsAgreed(false);
                onNo();
              }}
              className="absolute right-5 top-5 z-50 rounded-full p-2 text-muted-foreground hover:bg-black/5 hover:text-foreground transition"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>

            {/* Content */}
            <div className="relative z-10 flex min-h-[520px] items-center justify-center px-6 py-10">
              <KYCVerification />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
